/**
 * Delete Suppression Rule Confirmation Dialog
 *
 * Follows the JiraConfigDialog confirm-delete pattern.
 */

import { AlertTriangle, Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { useDeleteSuppressionRule } from "@/hooks/useSuppressionRules";
import type { SuppressionRule } from "@/types/suppressionRule";

interface DeleteSuppressionRuleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rule: SuppressionRule;
}

export function DeleteSuppressionRuleDialog({
  open,
  onOpenChange,
  rule,
}: DeleteSuppressionRuleDialogProps) {
  const deleteMutation = useDeleteSuppressionRule();

  async function handleDelete() {
    try {
      await deleteMutation.mutateAsync(rule.id);
      onOpenChange(false);
    } catch {
      // Error handled by mutation state
    }
  }

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!deleteMutation.isPending) onOpenChange(v); }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Delete Suppression Rule</DialogTitle>
          <DialogDescription>This action cannot be undone.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200">
            <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
            <div>
              <p className="font-medium">Delete "{rule.name}"?</p>
              <p className="text-xs mt-1">
                This rule will be removed and future issues matching its criteria
                will no longer be suppressed.
              </p>
            </div>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={deleteMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                "Delete Rule"
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
